import { httpClient } from "../http-client";
import { ApiResponse } from "../../types/responses/api-response";
import type { UserSearchSuggestionDto } from "../../api/generated/types.gen";
import type { UserListStatus } from "../../types/forms/user-forms";

// ===== USERS API =====

/**
 * AD-172 (решение Сергея №46): короче сервер отвечает на подсказку 400
 * (`USER_SEARCH_MIN_QUERY_LENGTH` в BE `user-search-suggestions.dto.ts`).
 * Пробелы по краям в длину не входят — сервер отбрасывает их до проверки.
 */
export const PEOPLE_SEARCH_MIN_QUERY_LENGTH = 3;

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  /**
   * AD-172. Здесь было `isActive: boolean` — такого поля у учётки на сервере
   * нет, и отметка «Неактивен» стояла бы у каждого найденного.
   */
  status: UserListStatus;
  emailVerified: boolean;
  avatar?: string;
  phone?: string;
  department?: string;
  position?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
  department?: string;
  position?: string;
  phone?: string;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  role?: string;
  department?: string;
  position?: string;
  phone?: string;
  isActive?: boolean;
}

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
  phone?: string;
  department?: string;
  position?: string;
  preferences: {
    language: string;
    theme: string;
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
  };
}

export interface UserSettings {
  language: string;
  theme: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  privacy: {
    profileVisibility: "public" | "private" | "team";
    showEmail: boolean;
    showPhone: boolean;
  };
}

export interface UserActivity {
  id: string;
  type: "login" | "logout" | "create" | "update" | "delete";
  description: string;
  timestamp: string;
  ip?: string;
  userAgent?: string;
}

export interface Department {
  id: string;
  name: string;
  description?: string;
  managerId?: string;
  memberCount: number;
}

/**
 * Внутренняя роль из словаря `internal_roles` (`GET /users/roles`).
 *
 * AD-93: `permissions` здесь было объявлено, но сервер
 * (`UsersService.getRoles`) не отдаёт такого поля вовсе — карточка ролей
 * звала `role.permissions.slice(0, 3)` и падала на первой же непустой
 * выдаче. `id` — это ИМЯ роли (SUPER_ADMIN, …), то самое, что принимают
 * `CreateUserDto.role` и `POST /auth/assign-role`; `name` — подпись для
 * человека; `description` в словаре обнуляемое.
 */
export interface Role {
  id: string;
  name: string;
  description: string | null;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  usersByRole: Record<string, number>;
  usersByDepartment: Record<string, number>;
  recentRegistrations: number;
  averageActivity: number;
}

export const usersApi = {
  // ===== УПРАВЛЕНИЕ ПОЛЬЗОВАТЕЛЯМИ =====

  // Список пользователей
  // ADR-002: data = User[], пагинация (total/page/limit) — в meta
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    department?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<User[]>> => {
    const limit = params?.limit ?? 20;
    const page = params?.page ?? 1;
    const queryParams: Record<string, unknown> = {
      skip: (page - 1) * limit,
      take: limit,
      ...params,
    };
    delete queryParams["page"];
    delete queryParams["limit"];
    return httpClient.get("/users", { params: queryParams });
  },

  // Создание пользователя
  createUser: async (data: CreateUserRequest): Promise<ApiResponse<User>> => {
    return httpClient.post("/users", data);
  },

  // Получение пользователя по ID
  getUser: async (id: string): Promise<ApiResponse<User>> => {
    return httpClient.get(`/users/${id}`);
  },

  // Обновление пользователя
  updateUser: async (
    id: string,
    data: UpdateUserRequest,
  ): Promise<ApiResponse<User>> => {
    return httpClient.patch(`/users/${id}`, data);
  },

  // Удаление пользователя
  deleteUser: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/users/${id}`);
  },

  // Блокировка пользователя
  blockUser: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.patch(`/users/${id}/block`);
  },

  // Разблокировка пользователя
  unblockUser: async (
    id: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.patch(`/users/${id}/unblock`);
  },

  // Смена пароля пользователя
  changeUserPassword: async (
    id: string,
    newPassword: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.patch(`/users/${id}/password`, { newPassword });
  },

  // Загрузка аватара
  uploadAvatar: async (
    id: string,
    file: File,
  ): Promise<ApiResponse<{ avatarUrl: string }>> => {
    const formData = new FormData();
    formData.append("avatar", file);
    return httpClient.post(`/users/${id}/avatar`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // ===== ПРОФИЛЬ ПОЛЬЗОВАТЕЛЯ =====

  // Профиль текущего пользователя
  getMyProfile: async (): Promise<ApiResponse<UserProfile>> => {
    return httpClient.get("/users/me/profile");
  },

  // Настройки пользователя
  getMySettings: async (): Promise<ApiResponse<UserSettings>> => {
    return httpClient.get("/users/me/settings");
  },

  // Обновление настроек
  updateMySettings: async (
    settings: Partial<UserSettings>,
  ): Promise<ApiResponse<UserSettings>> => {
    return httpClient.patch("/users/me/settings", settings);
  },

  // Активность пользователя
  getMyActivity: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
  }): Promise<ApiResponse<UserActivity[]>> => {
    return httpClient.get("/users/me/activity", { params });
  },

  // Уведомления пользователя
  getMyNotifications: async (params?: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
  }): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/users/me/notifications", { params });
  },

  // ===== ПОИСК И СТАТИСТИКА =====

  /**
   * Подсказка людей для приглашения — AD-172, модель в BE
   * `docs/adr/ADR-USER-SEARCH-suggestions-scope.md`.
   *
   * Маршрут открыт любому вошедшему, поэтому ответ узкий: по подстроке имени
   * находятся только люди событий, которыми ищущий управляет, любой другой — по
   * точному адресу почты; почты в ответе нет, имя и фамилия бывают пустыми.
   * `query` короче `PEOPLE_SEARCH_MIN_QUERY_LENGTH` сервер отклоняет (400).
   * Поиск администратора по всем учёткам — `getUsers({ search })`.
   */
  searchUsers: async (
    query: string,
  ): Promise<ApiResponse<UserSearchSuggestionDto[]>> => {
    return httpClient.get("/users/search/suggestions", {
      params: { q: query },
    });
  },

  /**
   * Список отделов. AD-93: сервер (`UsersService.getDepartments`) отдаёт
   * `string[]` — DISTINCT по свободной колонке `users.department`. Здесь было
   * объявлено `Department[]`, и оба потребителя читали у строки поле `.id`:
   * фильтр отдела уходил `undefined`, а выпадающий список печатал пустые
   * пункты. Отдела как сущности с идентификатором в базе нет.
   */
  getDepartments: async (): Promise<ApiResponse<string[]>> => {
    return httpClient.get("/users/departments");
  },

  // Список ролей
  getRoles: async (): Promise<ApiResponse<Role[]>> => {
    return httpClient.get("/users/roles");
  },

  // Статистика пользователей
  getUserStats: async (): Promise<ApiResponse<UserStats>> => {
    return httpClient.get("/users/stats");
  },

  // ===== ЭКСПОРТ/ИМПОРТ =====

  // Экспорт в CSV
  exportUsersCsv: async (params?: {
    role?: string;
    department?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<{ downloadUrl: string }>> => {
    return httpClient.get("/users/export/csv", { params });
  },

  // Экспорт в JSON
  exportUsersJson: async (params?: {
    role?: string;
    department?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<{ downloadUrl: string }>> => {
    return httpClient.get("/users/export/json", { params });
  },

  // Экспорт в Excel
  exportUsersExcel: async (params?: {
    role?: string;
    department?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<{ downloadUrl: string }>> => {
    return httpClient.get("/users/export/excel", { params });
  },

  // Импорт из CSV
  importUsersCsv: async (
    file: File,
  ): Promise<
    ApiResponse<{
      imported: number;
      errors: number;
      results: Array<{ success: boolean; message: string; data?: any }>;
    }>
  > => {
    const formData = new FormData();
    formData.append("file", file);
    return httpClient.post("/users/import/csv", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Импорт из JSON
  importUsersJson: async (
    file: File,
  ): Promise<
    ApiResponse<{
      imported: number;
      errors: number;
      results: Array<{ success: boolean; message: string; data?: any }>;
    }>
  > => {
    const formData = new FormData();
    formData.append("file", file);
    return httpClient.post("/users/import/json", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Импорт из Excel
  importUsersExcel: async (
    file: File,
  ): Promise<
    ApiResponse<{
      imported: number;
      errors: number;
      results: Array<{ success: boolean; message: string; data?: any }>;
    }>
  > => {
    const formData = new FormData();
    formData.append("file", file);
    return httpClient.post("/users/import/excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  acceptTermsOfService: (): Promise<ApiResponse<unknown>> => {
    return httpClient.post("/users/terms/accept");
  },
};

export default usersApi;
